import {
  useCallback, useEffect, useRef, useState,
} from 'react';

export interface PdfViewerControl {
  setZoom: (zoom: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  getZoom: () => void;
  currentZoom: number | null;
  passwordCancelled: boolean;
  resetPasswordCancelled: () => void;
  pagesLoaded: boolean;
  resetPagesLoaded: () => void;
}

export function usePdfViewerControl(iframeRef: React.RefObject<HTMLIFrameElement>): PdfViewerControl {
  const [currentZoom, setCurrentZoom] = useState<number | null>(null);
  const [passwordCancelled, setPasswordCancelled] = useState(false);
  const [pagesLoaded, setPagesLoaded] = useState(false);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // 複数のビューアが同時に存在しうるため、対象のiframeからのメッセージのみを処理する
      if (event.source !== iframeRef.current?.contentWindow) return;

      if (event.data && event.data.type === 'ccPdfStatus') {
        if (typeof event.data.zoom === 'number') {
          setCurrentZoom(event.data.zoom);
        }
        if (event.data.error === 'passwordCancelled') {
          setPasswordCancelled(true);
        }
        if (event.data.pagesLoaded === true) {
          setPagesLoaded(true);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [iframeRef]);

  const sendCommand = useCallback((action: string, value?: any) => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.postMessage({
        type: 'ccPdfControl',
        action,
        value,
      }, '*');
    }
  }, [iframeRef]);

  const setZoom = useCallback((zoom: number) => {
    sendCommand('setZoom', zoom);
  }, [sendCommand]);

  const zoomIn = useCallback(() => {
    sendCommand('zoomIn');
  }, [sendCommand]);

  const zoomOut = useCallback(() => {
    sendCommand('zoomOut');
  }, [sendCommand]);

  const getZoom = useCallback(() => {
    sendCommand('getZoom');
  }, [sendCommand]);

  const resetPasswordCancelled = useCallback(() => {
    setPasswordCancelled(false);
  }, []);

  const resetPagesLoaded = useCallback(() => {
    setPagesLoaded(false);
  }, []);

  return {
    setZoom,
    zoomIn,
    zoomOut,
    getZoom,
    currentZoom,
    passwordCancelled,
    resetPasswordCancelled,
    pagesLoaded,
    resetPagesLoaded,
  };
}
